/* eslint-disable eqeqeq */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import { read, utils } from 'xlsx';
import {
  getDistinctValues,
  toMoment,
  toFixed,
  listToString,
  excelSerialToMoment,
} from 'utils/normalizers';
import formatValues, { DATE_FORMAT, DATE_FRONTEND_FORMAT, DATETIME_FORMAT } from 'utils/formatValues';
import { getContenTypePorTipoDePadron, getTiposDeDocumentosDeTraslaciones } from 'api/catalogos';
import {
  getPredios,
  formatValoresPredio,
  dateKeysPredio,
  deletableKeysPredio,
} from 'api/catastro/predio';
import Uploader from 'components/Uploader';
import Select from 'components/Select';
import {
  tiposDeContribuyentesSolicitantesEnum,
  tiposDePasosEnum,
  procedenciasEnum,
} from 'views/Tramites/GPM/enums';
import getAllPages from 'utils/getAllPages';
import { getPlantillaTramite } from 'api/tramites/plantillasDeTramites';
import { SaveOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getConsultaTask } from 'api/utils/consultaTask';
import { getCatalogoPeritosDePredio } from 'api/catastro/peritosDePredios';
import { getCatalogoNotariosDePredios } from 'api/catastro/notariosDePredios';
import {
  getEdificiosDePredios,
  getEtapasDePredios,
  getManzanasDePredios,
  getTipoDePredio,
  getTiposDePrediosUrbanos,
  getUnidadesDePredios,
  getUsosDePredio,
  getTiposDeCuentasDePredios,
  getSiglasTiposDePropietarios,
  getFactoresDeTraslado,
  getTasasDeTrasladoDePredios,
  getValoresDeTerrenosYConstrucciones,
  getTasasDePredios,
} from 'api/catastro';
import removeIndexes from 'views/Tramites/GPM/removeIndexes';
import calculateValorBase from 'views/Catastro/utils/calculateValorBase';
import { getParticipes } from 'api/catastro/participes';
import { logger } from 'utils/debug';
import { getContribuyente } from 'api/empresas/contribuyente';
import { getBloquesDeTerrenoYConstruccion } from 'api/catastro/bloquesDeTerrenoYConstruccion';
import { postObtenerDocumento } from 'api/reporteador';
import Previewer from 'components/Previewer';

const DEV = process.env.NODE_ENV === 'development';

export const permissionConfig = {
  mainPermission: 'viewcrud_cargamasivatramites',
};

const gettersByFkFieldName = {
  edificio_id: (clave) => getEdificiosDePredios({ clave }),
  unidad_id: (clave) => getUnidadesDePredios({ clave }),
  manzana_id: (clave) => getManzanasDePredios({ clave }),
  etapa_id: (clave) => getEtapasDePredios({ clave }),
  tipo_de_predio_id: (clave) => getTipoDePredio({ clave }),
  tipo_de_predio_urbano_id: (clave) => getTiposDePrediosUrbanos({ clave }),
  tipo_de_documento_traslado_id: (val) => getTiposDeDocumentosDeTraslaciones({ descripcion: val }),
  uso_de_predio_id: (descripcion) => getUsosDePredio({ descripcion }),
  tipo_de_cuenta_id: (clave) => getTiposDeCuentasDePredios({ clave }),
  factor_traslado_id: (descripcion) => getFactoresDeTraslado({ descripcion }),
};
const not_found_fk = 'not_found_fk';
const mapQs = (res) => {
  if (res.some((r) => r.results)) return res.map((e) => e.results).flat(2);
  return res.flat();
};
const anio_tasa = parseInt(toMoment(new Date()).format('YYYY'), 10);
const ID_TRANSMISION = 1;
const ID_TRANSMISION_SIN_COBRO = 32;
const ID_AVALUO_VENTANILLA = 16;

const defaultsTransmision = {
  TIPO_DE_DOCUMENTO_TRASLADO_ID: 1,
  APLICA_AVALUO: true,
  APLICA_BENEFICIO_LEY: false,
  FECHA_ADJUDICACION_TRASLADO: null,
  ESTA_EXENTO: false,
  OMITIR_VALORES_REFERENCIADOS: true,
  TIPO_DE_CUENTA_ID: 2,
  PORCENTAJE_TRASLADO: 100,
  USO_DE_PREDIO_ID: 17,
};

const CargaMasivaTramites = () => {
  const [form] = Form.useForm();
  const archivo = Form.useWatch('archivo', form);
  const plantilla_id = Form.useWatch('plantilla', form);
  const contribuyente = Form.useWatch('contribuyente', form);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState();
  const [errors, setErrors] = useState([]);
  const [excelErrors, setExcelErrors] = useState();
  const [plantillas, setPlantillas] = useState([]);
  const [peritosDePredios, setPeritosDePredios] = useState([]);
  const [notariosDePredios, setNotariosDePredios] = useState([]);
  const [siglas, setSiglas] = useState([]);
  const [tasasMarginales, setTasasMarginales] = useState([]);
  const [avaluoConBloques, setAvaluoConBloques] = useState(false);
  const [base64, setBase64] = useState();
  const plantilla = plantillas.find((p) => p.id === plantilla_id);
  const transmision = [ID_TRANSMISION, ID_TRANSMISION_SIN_COBRO].includes(plantilla_id);
  const transmisionSinCobro = plantilla_id === ID_TRANSMISION_SIN_COBRO;
  const tipoDeContribuyente = plantilla?.tipo_de_contribuyente_solicitante;
  const esNotario = tipoDeContribuyente?.id === tiposDeContribuyentesSolicitantesEnum.NOTARIO;

  const tramite = {
    plantilla: plantilla?.id,
    tipo_de_padron: 3,
    content_type_a_padron: getContenTypePorTipoDePadron(3),
    solicitante_ciudadano: false,
    contribuyente,
    tipos_de_registros_de_tramite: 1,
    estados_globales: 2,
    fecha_de_inicio: toMoment(new Date()).format(DATE_FORMAT),
    procedencia: procedenciasEnum.LAYOUT,
    aplica_avaluo: plantilla?.aplica_avaluo,
    canal_de_pago: 2,
  };

  const parseFileAvaluos = async (workbook) => {
    const options = { range: 0, blankrows: false, defval: null };
    const _predios = utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], options);
    const terrenos = !workbook.Sheets['Bloques de Terreno'] ? null
      : utils.sheet_to_json(workbook.Sheets['Bloques de Terreno'], { ...options, range: 1 });
    const construcciones = !workbook.Sheets['Bloques de Construccion'] ? null
      : utils.sheet_to_json(workbook.Sheets['Bloques de Construccion'], { ...options, range: 1 });
    const _excelErrors = {
      predios: _predios.map((p) => ({
        clave_catastral_municipal: `${p.RECAUDADORA || p.REC}${p.PREDIO || p.SEC}${p.CUENTA}`,
        row: p.__rowNum__ + 1,
        errors: [],
      })),
      terrenoErrors: [],
      construccionesErrors: [],
      terrenosMessage: '',
      construccionesMessage: '',
    };
    const _result = {
      object_id_padron: [],
      folio_forma: _predios.map((p) => p.FOLIO_FORMA),
      predios: _predios.map((p) => ({
        id: null,
        requisitos: [],
        json_predio: null,
        folio_forma: p.FOLIO_FORMA,
      })),
      tramite,
      historial: [],
    };

    if (terrenos || construcciones) {
      const idx = plantilla.pasos_para_tramites
        .findIndex((p) => p.tipo_de_paso === tiposDePasosEnum.AFECTAR_PADRON_JSON);
      const pasos = plantilla.pasos_para_tramites.filter((_, i) => idx !== -1 && i < idx);
      _result.historial = pasos
        .map((p, i) => ({ paso: p?.id, paso_a_mandar: plantilla.pasos_para_tramites[i + 1]?.id }));
      _result.pasos_generar_documento = pasos
        .filter((p) => p.tipo_de_paso === tiposDePasosEnum.EMISION_DE_DOCUMENTO).map((p) => p.id);
    }

    const distinctClaves = getDistinctValues(_excelErrors.predios
      .map((p) => p.clave_catastral_municipal));
    if (distinctClaves.length !== _predios.length) {
      logger('[_predios]', _predios);
      logger('[distinctClaves]', distinctClaves);
      message.warning('Hay predios duplicados en el archivo');
      return;
    }

    setLoading(true);
    const distinctValores = getDistinctValues([
      ...(terrenos || []).map((t) => `${t.matriz_bloque}_${t.valor_unitario}_${t.anio}_${true}`),
      ...(construcciones || []).map((t) => `${t.matriz_bloque}_${t.valor_unitario}_${t.anio}_${false}`),
    ]).filter((e) => !e.includes('null'));
    const valores = (await Promise.all(distinctValores.map((matrizValor) => {
      const [matriz_valor, valor, anio, tipo_bloque] = matrizValor.split('_');
      return getValoresDeTerrenosYConstrucciones({
        matriz_valor,
        valor,
        anio,
        tipo_bloque,
      });
    }))).flat();

    (terrenos || []).forEach((terreno, index) => {
      terrenos[index].valor = valores.find((v) => v.valor == terreno.valor_unitario
        && v.matriz_valor == terreno.matriz_bloque)?.id || 'not_found_fk';
      if (terrenos[index].valor === 'not_found_fk') {
        _excelErrors.terrenoErrors.push(terreno.__rowNum__ + 1);
      }
    });
    _excelErrors.terrenosMessage = (
      <>
        Terrenos
        {!!_excelErrors.terrenoErrors.length && (
          <Tooltip title="Filas con errores, no se pudo encontrar llave foránea de valor, verifique la información">
            <QuestionCircleOutlined style={{ marginLeft: 3 }} />
          </Tooltip>
        )}
        {': '}
        {!terrenos ? 'Sin terrenos cargados'
          : listToString(_excelErrors.terrenoErrors.length ? _excelErrors.terrenoErrors : ['Sin errores en terrenos cargados'])}
      </>
    );
    (construcciones || []).forEach((terreno, index) => {
      construcciones[index].valor = valores.find((v) => v.valor == terreno.valor_unitario
        && v.matriz_valor == terreno.matriz_bloque)?.id || 'not_found_fk';
      if (construcciones[index].valor === 'not_found_fk') {
        _excelErrors.construccionesErrors.push(terreno.__rowNum__ + 1);
      }
    });
    _excelErrors.construccionesMessage = (
      <>
        Construcciones
        {!!_excelErrors.construccionesErrors.length && (
          <Tooltip title="Filas con errores, no se pudo encontrar llave foránea de valor, verifique la información">
            <QuestionCircleOutlined style={{ marginLeft: 3 }} />
          </Tooltip>
        )}
        {': '}
        {!construcciones ? 'Sin Construcciones cargadas'
          : listToString(_excelErrors.construccionesErrors.length ? _excelErrors.construccionesErrors : ['Sin errores en construcciones cargadas'])}
      </>
    );
    const predios = (await Promise
      .all(distinctClaves.map((c) => getPredios({ clave_catastral_municipal: c }))))
      .map((r, i) => r.results.find((p) => p.clave_catastral_municipal === distinctClaves[i]));
    const distinctTasas = getDistinctValues(predios.filter((p) => p)
      .map((p) => `${p.tipo_de_predio?.id}_${p.tipo_de_predio_urbano}`));
    const tasas = (await Promise.all(distinctTasas.map((params) => {
      const [tipo_de_predio, tipo_urbano] = params.split('_');
      return getTasasDePredios({ tipo_de_predio, tipo_urbano, anio_tasa });
    }))).flat();
    const bloques = (await Promise.all(predios.filter((p) => p)
      .map((p) => getBloquesDeTerrenoYConstruccion({ predio: p?.id })))).flat();

    _result.object_id_padron = _result.predios.map((p, idx) => {
      const predio = predios[idx];
      if (!predio) {
        _excelErrors.predios[idx].errors.push('No se encontró predio con clave brindada');
      }
      if (predio?.json_predio || predio?.estados_globales === 2) {
        _excelErrors.predios[idx].errors.push('Tiene trámite en proceso');
      }
      if (predio?.estados_globales === 5) {
        _excelErrors.predios[idx].errors.push('Predio cancelado');
      }
      if (predio?.alerta?.some((a) => a.es_restrictiva)) {
        _excelErrors.predios[idx].errors.push('Tiene alertas restrictivas');
      }
      if (!_result.folio_forma[idx]) {
        _excelErrors.predios[idx].errors.push('No se brindó folio forma');
      }
      return predio?.id;
    });
    if (terrenos || construcciones) {
      _result.object_id_padron.forEach((predio, index) => {
        _result.predios[index].id = predio;
        const json_predio = removeIndexes(predios[index]);
        const {
          TERRENO_DESDE, TERRENO_HASTA,
          CONSTRUCCION_DESDE, CONSTRUCCION_HASTA,
        } = _predios[index];
        const filter = (from, to) => (t) => from && to
          && t.__rowNum__ + 1 >= from && t.__rowNum__ + 1 <= to;
        const normalizeBloque = (bloque, idx) => {
          const valor_unitario = toFixed(bloque.valor_unitario, 3);
          const area = toFixed(bloque.area, 3);
          const factor_ajuste = toFixed(bloque.factor_ajuste, 3);
          return {
            id: `temp_id_${index}_${idx}`,
            ...bloque,
            valor_unitario,
            area,
            factor_ajuste,
            importe: toFixed(valor_unitario * area * factor_ajuste, 3),
            predio,
          };
        };
        const bloquesTerreno = (terrenos || []).filter(filter(TERRENO_DESDE, TERRENO_HASTA))
          .map((bloque, i) => ({ ...normalizeBloque(bloque, i), tipo_de_bloque: true }));
        const bloquesConstruccion = (construcciones || [])
          .filter(filter(CONSTRUCCION_DESDE, CONSTRUCCION_HASTA))
          .map((bloque, i) => ({ ...normalizeBloque(bloque, i), tipo_de_bloque: false }));
        if ((bloquesTerreno.length + bloquesConstruccion.length) > 4) {
          _excelErrors.predios[index].errors.push(`Bloques exceden el límite permitido de 4 bloques (terreno: ${bloquesTerreno.length} bloques, construcción: ${bloquesConstruccion.length} bloques)`);
        }
        if (!bloquesTerreno.length) {
          _excelErrors.predios[index].errors.push('No se brindaron bloques de terreno');
        }
        if (_predios[index].REQ_FORMA_UNIVERSAL === 'SI') _result.predios[index].requisitos.push(1);
        if (_predios[index].REQ_INE_DEL_PERITO === 'SI') _result.predios[index].requisitos.push(33);
        if (_predios[index].REQ_RECIBO_DE_PAGO === 'SI') _result.predios[index].requisitos.push(34);
        if (_predios[index].REQ_AVALUO_EXTERNO === 'SI') _result.predios[index].requisitos.push(35);
        const reduce = (arr, key) => arr.reduce((accum, curr) => accum + toFixed(curr[key], 3), 0);
        json_predio.valor_del_terreno = toFixed(reduce(bloquesTerreno, 'importe'), 3);
        json_predio.superficie_total_del_terreno = toFixed(reduce(bloquesTerreno, 'area'), 3);
        json_predio.valor_de_la_construccion = toFixed(reduce(bloquesConstruccion, 'importe'), 3);
        json_predio.superficie_de_la_construccion = toFixed(reduce(bloquesConstruccion, 'area'), 3);
        json_predio.valor_catastral = json_predio.valor_del_terreno
          + json_predio.valor_de_la_construccion;
        json_predio.bloques_de_terreno = bloquesTerreno;
        json_predio
          .bloques_de_terreno_a_agregar = bloquesTerreno.map((b) => b.id);
        json_predio.bloques_de_terreno_a_eliminar = bloques
          .filter((b) => b.tipo_de_bloque && b.predio === predio).map((b) => b.id);
        json_predio.bloques_de_construccion = bloquesConstruccion;
        json_predio
          .bloques_de_construccion_a_agregar = bloquesConstruccion.map((b) => b.id);
        json_predio.bloques_de_construccion_a_eliminar = bloques
          .filter((b) => !b.tipo_de_bloque && b.predio === predio).map((b) => b.id);
        json_predio.perito_contribuyente = contribuyente;
        const _tasa = tasas.find((t) => json_predio.valor_catastral <= t.limite_superior
          && json_predio.valor_catastral >= t.limite_inferior
          && t.tipo_de_predio === (json_predio.tipo_de_predio?.id || json_predio.tipo_de_predio)
          && t.tipo_urbano === json_predio.tipo_de_predio_urbano);
        if (_tasa?.cuota_fija) {
          json_predio.tasa = toFixed(_tasa.cuota_fija, 6);
        } else {
          _excelErrors.predios[index].errors.push('No se encontró tasa');
        }
        const formatted = formatValues(
          json_predio,
          { deletableKeys: deletableKeysPredio, clean: false },
          dateKeysPredio,
        );
        _result.predios[index].json_predio = JSON.stringify(formatted);
      });
      delete _result.object_id_padron;
      delete _result.folio_forma;
    } else {
      delete _result.predios;
      delete _result.historial;
    }
    setAvaluoConBloques(!!terrenos?.length);
    _excelErrors.predios = _excelErrors.predios.filter((p) => p.errors.length);
    _excelErrors.hasErrors = !!_excelErrors.predios.length || !!_excelErrors.terrenoErrors.length
      || !!_excelErrors.construccionesErrors.length;
    if (DEV) {
      form.setFieldValue(
        'result',
        JSON.stringify(_result, null, 2)
          .concat('\n\n')
          .concat(JSON.stringify(_excelErrors, null, 2))
          .concat('\n\n'),
      );
    }
    setResult(_result);
    setExcelErrors(_excelErrors);
    setLoading(false);
  };

  const parseFileTransmisiones = async (workbook) => {
    const normalizeKey = (key) => key.replace('*', '').toLocaleLowerCase().trim();
    const options = { range: 3, blankrows: false, defval: null };
    const _predios = utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], options);
    const _participes = utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[1]], options);
    setLoading(true);
    const notario = esNotario ? await getContribuyente(contribuyente) : null;
    const keys = Object.keys(_predios[0] || {}).concat(Object.keys(defaultsTransmision))
      .map((k) => k.trim()).filter((k) => !k.startsWith('__EMPTY'));
    const fkFields = keys.filter((k) => k.toLowerCase().endsWith('_id') && !(k in defaultsTransmision));
    const distinctQsMatrix = fkFields
      .map((fk) => _predios.map((_predio) => _predio[fk]))
      .map((arr) => getDistinctValues(arr).filter((e) => e));
    const distinctIndexedQsMatrix = (await Promise.all(
      fkFields.map((fk, idx) => Promise.all(distinctQsMatrix[idx].map(
        (q) => gettersByFkFieldName[normalizeKey(fk)](q),
      ))),
    )).map(mapQs);
    let prediosErrors = _predios.map(({ __rowNum__, ...p }, index) => {
      const clave_catastral_municipal = p[keys.find((k) => normalizeKey(k) === 'clave_catastral_municipal')];
      _predios[index].clave_catastral_municipal = clave_catastral_municipal;
      _predios[index] = {
        ..._predios[index],
        ...defaultsTransmision,
      };
      return {
        clave_catastral_municipal,
        row: __rowNum__ + 1,
        messages: [],
      };
    });
    const distinctClaves = getDistinctValues(_predios.map((p) => p.clave_catastral_municipal));
    if (distinctClaves.length !== _predios.length) {
      setLoading(false);
      message.warning('Hay predios repetidos o si clave catastral municipal');
      return;
    }
    const idxPago = plantilla.pasos_para_tramites
      .findIndex((p) => p.tipo_de_paso === tiposDePasosEnum.CARGOS);
    const historial = transmisionSinCobro ? []
      : plantilla.pasos_para_tramites.filter((_, i) => idxPago !== -1 && i < idxPago)
        .map((p, i) => ({ paso: p?.id, paso_a_mandar: plantilla.pasos_para_tramites[i + 1]?.id }));
    const predios = (await Promise
      .all(distinctClaves.map((c) => getPredios({ clave_catastral_municipal: c }))))
      .map((r, i) => r.results.find((p) => p.clave_catastral_municipal === distinctClaves[i]));
    const participesMatriz = await Promise.all(predios.map((p) => getParticipes({ predio: p.id })));
    _predios.forEach((_predio, index) => {
      if (!predios[index]) {
        prediosErrors[index]
          .messages = [`No se encontró predio ${_predio['* CLAVE_CATASTRAL_MUNICIPAL']}`];
        return;
      }
      if (predios[index].alerta?.some((a) => a.es_restrictiva)) {
        prediosErrors[index]
          .messages = [`Predio ${_predio['* CLAVE_CATASTRAL_MUNICIPAL']} tiene alertas restrictivas`];
      }
      const normalized = {
        predio_id: predios[index].id,
        predio: predios[index].id,
        json_predio: transmisionSinCobro ? {} : removeIndexes(predios[index]),
        requisitos: [],
      };
      keys.forEach((originalKey) => {
        const key = normalizeKey(originalKey);
        if (originalKey in defaultsTransmision) {
          normalized.json_predio[key.replace(/_id$/, '')] = _predio[originalKey];
        } else if (fkFields.includes(originalKey) && _predio[originalKey]) {
          const fkIndex = fkFields.indexOf(originalKey);
          const qIndex = distinctQsMatrix[fkIndex].indexOf(_predio[originalKey]);
          const val = distinctIndexedQsMatrix[fkIndex][qIndex]?.id || not_found_fk;
          if (val === not_found_fk) prediosErrors[index].messages.push(`no se encontró ${originalKey}`);
          normalized.json_predio[transmisionSinCobro ? key : key.replace(/_id$/, '')] = val;
        } else if (key.startsWith('req_')) {
          normalized[key] = _predio[originalKey];
        } else if (!key.startsWith('desde_') && !key.startsWith('hasta_')) {
          const _val = _predio[originalKey];
          // celdas con formato de fecha en Excel llegan como número serial
          const val = key.startsWith('fecha') && typeof _val === 'number'
            ? excelSerialToMoment(_val)?.format(DATE_FRONTEND_FORMAT) : _val;
          normalized.json_predio[key] = ['SI', 'NO'].includes(val) ? val === 'SI' : val;
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
            const fecha = toMoment(val, DATE_FRONTEND_FORMAT);
            if (fecha) {
              normalized.json_predio[key] = fecha
                .add(new Date().getTimezoneOffset() / 60, 'h');
            } else {
              normalized.json_predio[key] = null;
              prediosErrors[index].messages.push(`${originalKey} tiene una fecha inválida`);
            }
          } else if (key.startsWith('fecha') && val !== null && val !== undefined) {
            normalized.json_predio[key] = null;
            prediosErrors[index].messages.push(`${originalKey} debe tener formato ${DATE_FRONTEND_FORMAT}`);
          }
        } else if ((key.startsWith('desde_') || key.startsWith('hasta_')) && !_predio[originalKey]) {
          prediosErrors[index].messages.push(`no se brindó ${originalKey}`);
        }
      });
      const FILA = parseInt(_predio.FILA_PARTICIPE, 10);
      const _participe = _participes.find((p) => p.__rowNum__ + 1 >= FILA);
      if (!FILA) prediosErrors[index].messages.push('no se brindó participe');
      if (!_participe) prediosErrors[index].messages.push(`no se encontró participe de fila ${FILA}`);
      const participes = (_participe ? [_participe] : []).map((p) => {
        const {
          TIPO_DE_PERSONA,
          SIGLA_PROPIETARIO,
          RFC,
          NOMBRE_PARTICIPE,
          APELLIDO_PATERNO,
          APELLIDO_MATERNO,
          RAZON_SOCIAL,
        } = p;
        const participe = {
          tipo_de_persona: TIPO_DE_PERSONA,
          porcentaje_nuevo: 100,
          es_participe_principal: true,
          predio: normalized.predio,
          padron: 15,
          object_id_participe: null,
          nombre_participe: null,
          apellido_paterno: null,
          apellido_materno: null,
          descripcion: null,
        };
        if (SIGLA_PROPIETARIO) {
          participe.sigla_propietario = siglas
            .find((s) => s.descripcion === SIGLA_PROPIETARIO)?.id;
          if (!participe.sigla_propietario) {
            prediosErrors[index].messages
              .push(`no se encontró tipo de propietario ${SIGLA_PROPIETARIO} para participe`);
          }
        } else {
          prediosErrors[index].messages
            .push('no se brindó SIGLA_PROPIETARIO para participe');
        }
        if (!RFC) prediosErrors[index].messages.push('no se brindó RFC para participe');
        if (participe.tipo_de_persona === 1) {
          participe.nombre_participe = NOMBRE_PARTICIPE;
          participe.apellido_paterno = APELLIDO_PATERNO;
          participe.apellido_materno = APELLIDO_MATERNO;
          participe.descripcion = [APELLIDO_PATERNO, APELLIDO_MATERNO, NOMBRE_PARTICIPE]
            .filter((e) => e).join(' ').trim();
        } else if (participe.tipo_de_persona === 2) {
          participe.descripcion = RAZON_SOCIAL;
        }
        if (participe.tipo_de_persona === 1 && !participe.nombre_participe) {
          prediosErrors[index].messages.push('no se brindó NOMBRE_PARTICIPE para participe');
        }
        if (participe.tipo_de_persona === 1 && !participe.apellido_paterno) {
          prediosErrors[index].messages.push('no se brindó APELLIDO_PATERNO para participe');
        }
        if (participe.tipo_de_persona === 2 && !participe.descripcion) {
          prediosErrors[index].messages.push('no se brindó RAZON_SOCIAL para participe');
        }

        if (!transmisionSinCobro) {
          participe.object_id_participe = RFC;
          participe.id = `temp_id_${index}`;
          normalized.contribuyente = {
            rfc: RFC,
            tipo_de_persona: participe.tipo_de_persona,
            nombre: participe.nombre_participe,
            apellido_paterno: participe.apellido_paterno,
            apellido_materno: participe.apellido_materno,
            razon_social: participe.descripcion,
            nombre_completo: participe.descripcion,
          };
        } else {
          participe.rfc = RFC;
        }
        return participe;
      });
      if (transmisionSinCobro) {
        normalized.participes = participes;
      } else {
        normalized.json_predio.participes = participes;
        normalized.json_predio.participes_a_eliminar = participesMatriz[index].map((p) => p.id);
        normalized.json_predio.participes_a_agregar = [participes[0].id];
        normalized.json_predio.participe_principal_anterior = participesMatriz[index]
          .find((p) => p.es_participe_principal)?.id;
      }
      const mergePredio = { ...predios[index], ...normalized.json_predio };
      const valoresPredio = formatValoresPredio(mergePredio);
      Object.keys(valoresPredio).forEach((key) => {
        if (key in normalized.json_predio) {
          normalized.json_predio[key] = valoresPredio[key];
        }
        if (Number.isNaN(valoresPredio[key])) {
          prediosErrors[index].messages.push(`${key} tiene un valor NaN`);
        }
      });
      normalized.json_predio = {
        ...normalized.json_predio,
        ...calculateValorBase(mergePredio, mergePredio),
      };
      if (esNotario) {
        normalized.json_predio.lugar_notaria = notario?.direcciones
          ?.codigo_postal?.municipio?.nom_mun;
        normalized.json_predio[transmisionSinCobro ? 'notario_contribuyente_id' : 'notario_contribuyente'] = contribuyente;
      }
      const valor = normalized.json_predio.valor_catastral || predios[index].valor_catastral;
      const tasa = tasasMarginales
        .find((t) => valor <= t.limite_superior && valor >= t.limite_inferior);
      normalized.json_predio.tasa_marginal = tasa?.tasa_marginal;
      if (Number.isNaN(normalized.json_predio.valor_base_traslado)) {
        prediosErrors[index].messages.push('cálculo de valor base es incorrecto');
      }
      normalized.tramite = {
        ...tramite,
        object_id_padron: normalized.predio,
        estados_globales: transmisionSinCobro ? 4 : 2,
        fecha_de_finalizacion: transmisionSinCobro
          ? toMoment(new Date()).format(DATETIME_FORMAT) : undefined,
      };
      normalized.historial = historial;
      if (normalized.req_aviso_traslado === 1) normalized.requisitos.push(2);
      if (normalized.req_no_adeudo_predial === 1) normalized.requisitos.push(4);
      if (normalized.req_avaluo_catastral === 1) normalized.requisitos.push(6);
      if (normalized.req_no_adeudo_agua === 1) normalized.requisitos.push(5);
      if (normalized.req_copias_escritura === 1) normalized.requisitos.push(8);
      delete normalized.req_aviso_traslado;
      delete normalized.req_no_adeudo_predial;
      delete normalized.req_avaluo_catastral;
      delete normalized.req_no_adeudo_agua;
      delete normalized.req_copias_escritura;
      const formatted = formatValues(
        normalized.json_predio,
        { deletableKeys: deletableKeysPredio, clean: false },
        dateKeysPredio,
      );
      if (transmisionSinCobro) {
        normalized.predio = formatted;
        normalized.json_predio = undefined;
      } else {
        normalized.predio_id = undefined;
        normalized.json_predio = JSON.stringify(formatted);
      }
      prediosErrors[index].errors = prediosErrors[index].messages;
      predios[index] = normalized;
    });
    prediosErrors = prediosErrors.filter((p) => p.messages.length);
    setResult({ predios });
    setExcelErrors({ predios: prediosErrors });

    if (DEV) {
      form.setFieldValue(
        'result',
        JSON.stringify(predios, null, 2)
          .concat('\n\n')
          .concat('Errores en predios: ')
          .concat(JSON.stringify(prediosErrors, null, 2))
          .concat('\n\n'),
      );
    }
    form.setFieldValue('predios', predios);
    setLoading(false);
  };

  const onCancel = () => {
    setResult();
    setAvaluoConBloques(false);
    setExcelErrors();
    setErrors([]);
    form.setFieldValue('result', '');
  };

  useEffect(() => {
    if (!archivo) {
      onCancel();
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = read(data, { type: 'array' });
      (transmision ? parseFileTransmisiones : parseFileAvaluos)(workbook);
    };
    reader.readAsArrayBuffer(archivo.file);
  }, [archivo]);

  useEffect(() => {
    const fetch = async () => {
      const [
        _plantillas,
        _peritosDePredios,
        _notariosDePredios,
        _siglas,
        _tasasMarginales,
      ] = await Promise.all([
        Promise.all([
          getPlantillaTramite(ID_AVALUO_VENTANILLA),
          getPlantillaTramite(ID_TRANSMISION),
          getPlantillaTramite(ID_TRANSMISION_SIN_COBRO),
        ]),
        getAllPages(getCatalogoPeritosDePredio, { page_size: 500 }),
        getAllPages(getCatalogoNotariosDePredios, { page_size: 500 }),
        getSiglasTiposDePropietarios(),
        getTasasDeTrasladoDePredios({ periodo: toMoment(new Date()).format('YYYY') }),
      ]);
      setPlantillas(_plantillas.map((p) => ({
        ...p, pasos_para_tramites: p?.pasos_para_tramites?.sort((a, b) => a.orden - b.orden) || [],
      })));
      setPeritosDePredios(_peritosDePredios.map((e) => ({
        ...e,
        disabled: !e.vigente,
        tooltip: !e.vigente && `Perito sin vigencia (${e.fecha_vigencia?.format(DATE_FRONTEND_FORMAT)})`,
      })));
      setNotariosDePredios(_notariosDePredios);
      setSiglas(_siglas);
      setTasasMarginales(_tasasMarginales);
      setLoading(false);
    };
    fetch();
  }, []);

  const onFinish = async () => {
    const getURL = () => {
      if (transmisionSinCobro) return '/tramites/tramites/importacion-masiva-transmisiones-sin-cobro/';
      if (transmision) return '/tramites/tramites/importacion-masiva-transmisiones/';
      if (avaluoConBloques) return '/tramites/tramites/importacion-masiva-avaluos-con-bloques/';
      return '/tramites/tramites/importar-masivo/';
    };
    const getPayload = () => {
      if (avaluoConBloques) return result;
      if (transmision) return result.predios;
      return {
        ...tramite,
        object_id_padron: result.object_id_padron,
        folio_forma: result.folio_forma,
        historial: result.historial,
      };
    };
    setLoading(true);
    const [data, errorData] = await getConsultaTask(
      getPayload(),
      getURL(),
      null,
      null,
      null,
      true,
      null,
      message.info,
    );
    if (data) {
      if (data.folio_transaccion) {
        const [err, _base64] = await postObtenerDocumento({
          ID: 190,
          Parametros: [
            {
              Nombre: 'tramite',
              TipoParametro: 'Texto',
              Valor: data.folio_transaccion,
            },
          ],
        });
        setBase64(_base64);
        if (err) setTimeout(() => message.warning(err));
      }
      setResult();
      form.resetFields();
    }
    message[data ? 'success' : 'warning'](data ? 'Trámites creados correctamente'
      : 'No fue posible generar los trámite. Revise los detalles descritos en pantalla');
    setErrors((errorData?.errors || (Array.isArray(errorData) && errorData) || [])
      .map((e, i) => ({ ...e, key: e.object_id_padron || i })));
    setLoading(false);
  };

  const dataSourceSolicitantes = {
    [tiposDeContribuyentesSolicitantesEnum.NOTARIO]: notariosDePredios,
    [tiposDeContribuyentesSolicitantesEnum.PERITO]: peritosDePredios,
  }[tipoDeContribuyente?.id] || [];

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Form form={form} onFinish={onFinish} onReset={onCancel} layout="vertical">
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="plantilla"
              label="Trámite"
              rules={[{ required: true }]}
            >
              <Select
                labelProp="nombre"
                dataSource={plantillas}
                disabled={!!contribuyente || !!archivo}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="contribuyente"
              label={tipoDeContribuyente?.descripcion || 'Perito/Notario'}
              rules={[{ required: true }]}
            >
              <Select
                keyLabelRender
                labelProp="nombre_completo"
                dataSource={dataSourceSolicitantes}
                disabled={!plantilla || !!archivo}
              />
            </Form.Item>
          </Col>
          <Uploader
            title="Archivo"
            formItemName="archivo"
            rules={[{ required: true }]}
            allowedExtensions={['xlsx']}
            limitMB={Number.MAX_SAFE_INTEGER}
            disabled={!contribuyente}
          />
          <Col xs={24} sm={24} md={8}>
            <Form.Item>
              <Button
                block
                htmlType="submit"
                disabled={!!excelErrors?.hasErrors}
              >
                Guardar
                <SaveOutlined />
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item>
              <Button htmlType="reset" block>
                Limpiar
                <DeleteOutlined />
              </Button>
            </Form.Item>
          </Col>
        </Row>
        {!!result && (
          <ul>
            {excelErrors?.predios.map((p) => (
              <li key={p.row}>
                {`Fila: ${p.row} (${p.clave_catastral_municipal}):`}
                {Array.isArray(p.errors) ? (
                  <ul>{p.errors.map((e) => (<li key={e}>{e}</li>))}</ul>
                ) : p.errors}
              </li>
            ))}
            {!excelErrors?.predios.length && <li>Sin errores en hoja de predios</li>}
            {excelErrors?.terrenosMessage && <li>{excelErrors?.terrenosMessage}</li>}
            {excelErrors?.construccionesMessage && <li>{excelErrors?.construccionesMessage}</li>}
          </ul>
        )}
        {!!errors.length && (
          <>
            <Typography.Title level={4}>
              El servicio retorno los siguientes errores, verifique la información.
            </Typography.Title>
            <ul>
              {errors.map(({ key, ...item }) => (
                <li key={key}>
                  <b>{item.clave_catastral_municipal}</b>
                  {': '}
                  {item.detail || (DEV && JSON.stringify(item))}
                </li>
              ))}
            </ul>
          </>
        )}
        {DEV && (
          <Form.Item name="result">
            <Input.TextArea disabled autoSize={{ minRows: 20 }} />
          </Form.Item>
        )}
      </Form>
      <Previewer
        visible={Boolean(base64)}
        setVisible={() => setBase64()}
        base64={base64}
      />
    </Spin>
  );
};

export default CargaMasivaTramites;
